/**
 * updated by Alex Xia on 4/20/2022.
 * Description:
 *
 * ------ maintenance history ------
 * 04/20/2022   Alex      Use localstorage to store token and support sso for office add-in
 * 12/20/2022   Alex      Get existing token from server by profile email and set it to local storage for office add-in
 */
import { Injectable } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot, Router, CanActivate } from '@angular/router';
import { Observable } from 'rxjs';
import { AppConfig } from '@tamalelibs/models/app-config.model';
import { AuthHelperService } from '../../services/auth-helper.service';
import { AddInUtilities } from '../../services/utilities/addin-utilities';
import { AddInDepositType } from '../../models/addin.model';
import { businessConstants } from '../../constants/business.constants';
import { StorageService } from '../../services/storage.service';
import { SystemUser } from '@tamalelibs/models/user.model';
import { AppState } from '../../redux';
import { Store } from '@ngrx/store';
import { AuthActionTypes } from '../../redux/actions/auth.actions';
import { OfficeAddinService } from '../../services/office-addin-service';
import { AuthService } from '@tamalelibs/services/auth.service';

@Injectable({
    providedIn: 'root'
})
export class HomePluginGuard implements CanActivate {
    constructor(
        private _authService: AuthService,
        private _authHelper: AuthHelperService,
        private _officeAddinService: OfficeAddinService,
        private _storageService: StorageService,
        private _store: Store<AppState>,
        private _router: Router
    ) { }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        const self = this;
        // if it's outlook addin, try to get existing token from server and  save it to local storage
        if (AddInUtilities.isOfficeEnvironment() && Office.context && Office.context.mailbox && Office.context.mailbox.userProfile && !AppConfig.token) {
            const emailAddress = Office.context.mailbox.userProfile.emailAddress;
            if (emailAddress) {
                return new Observable<boolean>((observer => {
                    const subscription$ = self._authService.getExistedTokenFromServer(emailAddress).subscribe(response => {
                        if (subscription$) {
                            subscription$.unsubscribe();
                        }
                        if (response) {
                            const user = SystemUser.parse(response);
                            if (user && user.id && user.token) {
                                // make sure retrieve new token from local storage in home-plugin.guard.
                                AppConfig.token = '';
                                // refresh token in local storage for word/excel/ppt add-in usage.
                                localStorage.setItem('user', JSON.stringify(user));
                            }
                            observer.next(self.handleAuth(state));
                            observer.complete();
                        }
                    }, error => {
                        observer.next(self.handleAuth(state));
                        observer.complete();
                    });
                }));
            }
        } else {
            return new Observable<boolean>((observer => {
                observer.next(self.handleAuth(state));
                observer.complete();
            }));
        }
    }

    private handleAuth(state: any): boolean {
        // use local storage to store data to avoid logging in again for different office apps
        // we need to set user and current user data to store for future usage
        const self = this;
        let user = new SystemUser();
        let token = this._authHelper.getToken();
        let userId = this._authHelper.getUserId();
        let userInfo = this._authHelper.getUserFromSsoCookieInfo();
        const ssoSetting = this._authHelper.getSsoSettings();
        const fromUrl = this._authHelper.getFromUrl();

        if (AddInUtilities.isOfficeEnvironment()) {
            // get user from local storage, and set it to cookie to solve cookie loss issue on web office 365
            if (localStorage.getItem('user')) {
                user = JSON.parse(localStorage.getItem('user'));
            } else if (userInfo) {
                user = userInfo.user;
            }

            if (user && user.id) {
                AppConfig.token = user.token;
                AppConfig.username = user.loginName;
                AppConfig.userId = user.id;
                this._storageService.setItem('user', user);
                this._store.dispatch({
                    type: AuthActionTypes.LOGIN_SUCCESS,
                    payload: user
                });
            }

            // on web office 365, it's not supported to get or set cookie
            // so in some cases, the token is null while AppConfig.token has value.
            // https://github.com/OfficeDev/office-js/issues/1822
            token = AppConfig.token;
            userId = AppConfig.userId;
        }

        if (!token) {
            if (AddInUtilities.isOfficeEnvironment()) {
                if (ssoSetting.ssoEnabled) {
                    const options = {
                        height: 90,
                        width: 40,
                        displayInIframe: false // set displayInIframe to true can help improve performance. however, it will cause web office 365 fails to open dialogbox.
                    };
                    // display dialog box for the user to log in OKTA
                    // siteUrl is expected as https://server/msaddin/
                    const siteUrl = `${window.location.origin}${window.location.pathname}`;
                    Office.context.ui.displayDialogAsync(siteUrl,
                        options,
                        function (asyncResult) {
                            const dialog = asyncResult.value;
                            dialog.addEventHandler(Office.EventType.DialogEventReceived, processMessage);
                            dialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessage);
                            // set an interval job to check if SSO auth has completed by checking the existence of the user key in cookie. on web outlook, the user could not be set to localstorage on taskpane when the user is set on dialogbox
                            if (self._officeAddinService.isSafariOrFireFoxBrowser()) {
                                const checkUsrJob = setInterval(() => {
                                    userInfo = self._authHelper.getUserFromSsoCookieInfo();
                                    if (userInfo && userInfo.user) {
                                        // found user info, so assume passing the sso auth.
                                        // close the dialog
                                        dialog.close();
                                        let depositType: string = AddInDepositType.ADDINALL;
                                        if (localStorage.getItem(businessConstants.officeAddIn.depositType)) {
                                            depositType = localStorage.getItem(businessConstants.officeAddIn.depositType);
                                        }
                                        clearInterval(checkUsrJob);
                                        window.location.replace(siteUrl + '#/' + depositType.toLowerCase());
                                    }
                                }, 500);
                            }

                            // callback function to process message from dialog box
                            function processMessage(arg) {
                                if (arg.message) {
                                    // set user value received from sso.component to local storage, avoid propmting dialog box next time.
                                    localStorage.setItem('user', arg.message);
                                    // close the dialog
                                    dialog.close();

                                    let depositType: string = AddInDepositType.ADDINALL;
                                    if (localStorage.getItem(businessConstants.officeAddIn.depositType)) {
                                        depositType = localStorage.getItem(businessConstants.officeAddIn.depositType);
                                    }
                                    window.location.replace(siteUrl + '#/' + depositType.toLowerCase());

                                } else if (arg.error) {
                                    if (arg.error === businessConstants.officeAddIn.dialogBoxCloseCode) {
                                        // close dialog and hide task pane when user close the dialog box manually
                                        dialog.close();
                                        // don't close taskpane when dialogbox is closed to fix safari bug on web office
                                        if (!self._officeAddinService.isSafariOrFireFoxBrowser()) {
                                            AddInUtilities.closeTaskPaneForAddIn();
                                        }
                                    } else {
                                        self._router.navigate(['error']);
                                    }
                                }
                            }
                        });
                } else {
                    this._authHelper.redoAuthorization(ssoSetting, state.url, fromUrl);
                    return false;
                }

            } else {
                this._authHelper.redoAuthorization(ssoSetting, state.url, fromUrl);
                return false;
            }
        } else {
            AppConfig.token = token;
            AppConfig.userId = userId;
            return true;
        }
    }
}

